/**
 * Hauler Slot Component
 * Visual representation of an automated fish-selling hauler
 */

import { useState, useEffect } from 'react';
import { HaulerBuddy } from '@/types';
import { haulerManager } from '@/systems';
import { BuddyProgressionPanel } from '../progression/BuddyProgressionPanel';

interface HaulerSlotProps {
  hauler: HaulerBuddy;
}

export function HaulerSlot({ hauler }: HaulerSlotProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(hauler.name || '');
  const [currentProgress, setCurrentProgress] = useState(0);

  const handleSaveName = () => {
    haulerManager.renameHauler(hauler.slotNumber, editName);
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditName(hauler.name || '');
    setIsEditingName(false);
  };

  const handleForceHaul = () => {
    haulerManager.forceHaul(hauler.slotNumber);
  };

  const displayName = hauler.name || `Hauler ${hauler.slotNumber}`;

  // Calculate hauler's effective stats
  const effectiveCapacity = haulerManager.calculateEffectiveCapacity(hauler);
  const effectiveTravelTime = (haulerManager.calculateTravelTime(hauler) / 1000).toFixed(1);

  // Smooth progress bar update
  useEffect(() => {
    if (!hauler.stateStartedAt || !hauler.stateCompletesAt) {
      setCurrentProgress(0);
      return;
    }

    const updateProgress = () => {
      const now = Date.now();
      const total = hauler.stateCompletesAt! - hauler.stateStartedAt!;
      const elapsed = now - hauler.stateStartedAt!;
      const progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
      setCurrentProgress(progress);
    };

    // Update immediately
    updateProgress();

    // Then update every 100ms for smooth animation
    const interval = setInterval(updateProgress, 100);
    return () => clearInterval(interval);
  }, [hauler.stateStartedAt, hauler.stateCompletesAt, hauler.state]);

  // State-based styling
  const stateColors: Record<HaulerBuddy['state'], string> = {
    idle: 'bg-gray-600',
    loading: 'bg-yellow-500',
    traveling: 'bg-blue-500',
    selling: 'bg-green-500',
    returning: 'bg-purple-500',
  };

  const stateLabels: Record<HaulerBuddy['state'], string> = {
    idle: 'Idle',
    loading: 'Loading',
    traveling: 'Traveling',
    selling: 'Selling',
    returning: 'Returning',
  };

  const stateEmojis: Record<HaulerBuddy['state'], string> = {
    idle: '💤',
    loading: '📦',
    traveling: '🚶',
    selling: '💰',
    returning: '🔄',
  };

  return (
    <div className="relative">
      {/* Main hauler card */}
      <div
        className={`relative p-6 rounded-xl border-2 transition-all ${
          hauler.enabled
            ? 'bg-orange-900/30 border-orange-500/50 shadow-lg shadow-orange-500/20'
            : 'bg-gray-800/50 border-gray-600/50'
        }`}
      >
        {/* Header: Name, Status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🚚</span>
            <div>
              {!isEditingName ? (
                <h3
                  className="text-lg font-bold text-white cursor-pointer hover:text-orange-400 transition-colors"
                  onClick={() => setIsEditingName(true)}
                  title="Click to rename"
                >
                  {displayName}
                </h3>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-orange-500"
                    placeholder="Hauler name"
                    maxLength={20}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs transition-colors"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs transition-colors"
                  >
                    ✗
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1">
                <div
                  className={`text-xs px-2 py-1 rounded font-bold ${stateColors[hauler.state]} text-white`}
                >
                  {stateEmojis[hauler.state]} {stateLabels[hauler.state]}
                </div>
              </div>
            </div>
          </div>

          {/* Toggle Button */}
          <button
            onClick={() => haulerManager.toggleHauler(hauler.slotNumber)}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
              hauler.enabled
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {hauler.enabled ? 'Pause' : 'Resume'}
          </button>
        </div>

        {/* Progress bar for current action */}
        {hauler.enabled && hauler.state !== 'idle' && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{stateLabels[hauler.state]}</span>
              <span>{currentProgress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-100 ${stateColors[hauler.state]}`}
                style={{ width: `${currentProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Cargo display */}
        <div className="mb-3 p-3 bg-gray-700/50 rounded">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-gray-400">Cargo</div>
            {hauler.state === 'idle' && hauler.currentCargoCount === 0 && (
              <button
                onClick={handleForceHaul}
                className="px-2 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs font-bold transition-colors"
                title="Force hauler to collect and haul fish immediately"
              >
                Haul Now
              </button>
            )}
          </div>
          <div className="text-lg font-bold text-white">
            {hauler.currentCargoCount} / {effectiveCapacity} fish
          </div>
          {hauler.currentCargo.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">
              Carrying: {hauler.currentCargo.map(s => `${s.count}× ${s.quality} ${s.definitionId}`).join(', ')}
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="mb-3 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Trips</div>
            <div className="text-white font-bold">{hauler.totalTripsCompleted}</div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Fish Sold</div>
            <div className="text-white font-bold">{hauler.totalFishSold}</div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Value Generated</div>
            <div className="text-white font-bold">
              {hauler.totalValueGenerated.toFixed(0)}g
            </div>
          </div>
          <div className="bg-gray-700/50 p-2 rounded">
            <div className="text-gray-400">Travel Time</div>
            <div className="text-white font-bold">{effectiveTravelTime}s</div>
          </div>
        </div>

        {/* Effective stats display */}
        <div className="mt-3 p-3 bg-gray-700/30 rounded border border-gray-600">
          <div className="text-xs font-bold text-gray-300 mb-2">Effective Stats</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-gray-400">Capacity</div>
              <div className="text-white font-bold">{effectiveCapacity} fish</div>
            </div>
            <div>
              <div className="text-gray-400">Round Trip</div>
              <div className="text-white font-bold">{effectiveTravelTime}s</div>
            </div>
          </div>
        </div>

        {/* Progression Panel */}
        <div className="mt-4">
          <BuddyProgressionPanel buddy={hauler} />
        </div>
      </div>
    </div>
  );
}
